<!-- InterfaceMonitoringSection.vue -->
<template>
  <div 
    v-if="(configType === 'collectorconfig') || (isStaff && configType === 'workspaceconfig')"  
    class="space-y-6 glass-panel dark:text-bg-gray"
  >
    <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">
      Interface Monitoring
    </h2>
    
    <!-- File Upload Section -->
    <div class="flex grid grid-cols-1 md:grid-cols-2 gap-6">
      <div v-if="isStaff && isNewConfig && configType === 'collectorconfig'">
        <label for="file" class="block text-sm font-medium text-gray-700 dark:text-white">
          Interface Monitoring File
        </label>
        <input
          type="file"
          id="workspace_file"
          ref="fileInput"
          accept=".conf,.yml,.yaml,.toml"
          @change="handleFileChange"
          class="hidden"
        />
        <div class="flex space-x-2 dark:bg-gray-700 bg-gray-200 rounded truncate">
          <button
            type="button"
            @click="$refs.fileInput.click()"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Choose File
          </button>
          <div v-if="workspaceFile?.file" class="mt-2">
            <a
              class="hover:no-underline dark:hover:text-indigo-300"
              :href="workspaceFile.file"
              target="_blank"
            >
              {{ workspaceFile.file?.name || workspaceFile.file }}
            </a>
          </div>
        </div>
        <FieldError :message="errors?.workspace_file"/>
      </div>
    </div>
    
    <!-- Configuration Fields -->
    <div 
      v-if="(!isGeneralFileUpload && configType === 'workspaceconfig') || (!isWorkspaceFileUpload && configType === 'collectorconfig')" 
      class="flex grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <!-- Crit Host -->
      <div v-if="isStaff" class="flex flex-col">
        <label for="crit_host" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Crit Host <span class="text-red-500">*</span></span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              The target hostname or IP address to monitor for critical network checks
            </div>
          </div>
        </label>
        <input
          v-model="modelValue.crit_host"
          id="crit_host"
          @focus="emit('clear-error', 'crit_host')"
          :class="[
            'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
            errors?.crit_host
              ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
          ]"
          placeholder="e.g cloud.mivu.io:443"
        />
        <FieldError :message="errors?.crit_host"/>
      </div>

      <!-- Crit Proto -->
      <div v-if="isStaff" class="flex flex-col">
        <label for="crit_proto" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Crit Proto <span class="text-red-500">*</span></span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              The protocol (e.g., TCP, UDP) used to check connectivity to the critical host.
            </div>
          </div>
        </label>
        <GlassSelect
          v-model="modelValue.crit_proto"
          id="crit_proto"
          :options="protocols"
          placeholder="Select Protocol"
          :error="!!errors?.crit_proto"
          @update:model-value="emit('clear-error', 'crit_proto')"
        />
        <FieldError :message="errors?.crit_proto"/>
      </div>

      <!-- Gateway Ping IP -->
      <div v-if="isStaff" class="flex flex-col">
        <label for="gatewayping_ip" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Custom Gateway Ping IP</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              IP address used to test if the network gateway is reachable via a ping check.
            </div>
          </div>
        </label>
        <input 
          v-model="modelValue.gatewayping_ip"
          id="gatewayping_ip"
          placeholder="192.135.23.153"
          :class="[
            'border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
            errors?.gatewayping_ip ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
          ]"
        />
        <FieldError :message="errors?.gatewayping_ip"/>
      </div>

      <!-- Gateway Ping Toggle -->
      <div class="flex flex-col">
        <label for="gatewayping_bool" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Gateway Ping</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Enable or disable ping checks to monitor gateway status
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            type="checkbox" 
            id="gatewayping_bool" 
            :disabled="!workspaceMetricsEnabled"
            v-model="modelValue.gatewayping_bool"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="gatewayping_bool"
            id="gatewayping_bool_toggle" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
      </div>

      <!-- Polling Toggle -->
      <div v-if="isStaff" class="flex flex-col">
        <label for="polling_bool" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Polling</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Enable or disable general device polling
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="modelValue.polling_bool"
            id="polling_bool"
            type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="polling_bool" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
      </div>

      <!-- WiFi Toggle -->
      <div class="flex flex-col">
        <label for="wifi_bool" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Wifi</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Enable or disable wifi status
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="modelValue.wifi_bool"
            :disabled="!workspaceMetricsEnabled"
            id="wifi_bool"
            type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="wifi_bool"
            id="wifi_bool_toggle" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import FieldError from '../FieldError.vue'
import GlassSelect from "@/components/GlassSelect.vue"

defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  workspaceFile: {
    type: Object,
    default: null
  },
  protocols: {
    type: Array,
    required: true
  },
  configType: {
    type: String,
    required: true
  },
  isStaff: {
    type: Boolean,
    default: false
  },
  isNewConfig: {
    type: Boolean,
    default: false
  },
  isGeneralFileUpload: {
    type: Boolean,
    default: false
  },
  isWorkspaceFileUpload: {
    type: Boolean,
    default: false
  },
  workspaceMetricsEnabled: {
    type: Boolean,
    default: true
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'file-change', 'clear-error'])

function handleFileChange(event) {
  emit('file-change', event)
}
</script>